import Image from "next/image";
import Link from "next/link";
import { faClock, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatBangkokTime, formatThaiDate } from "./DateFormat";
import GameCapsuleTag from "./GameCapsuleTag";
import ImageCard from "./ImageCard";
import { useRef, useState } from "react";
import guideImage from "../../assets/images/guide.webp";
import SaveButton from "./SaveButton";
import { ReactionSummary } from "./ReactionBar";
import { cardImageLoader } from "../../utils/imageLoader";
import { useNearViewport } from "../../utils/useNearViewport";
import AuthorAvatar from "./AuthorAvatar";

import { DEFAULT_AVATAR } from "../../utils/image";

// One responsive tree per card.
//
// This card used to render three copies of itself — desktop, laptop, mobile —
// and hide two with CSS, inside sections that did the same, so a homepage with
// ~50 visible cards carried 408 <img> elements and 6,300 DOM nodes; hydration
// and style work scaled with all of them. The breakpoint differences are
// expressed with responsive classes on a single tree instead. Every width,
// margin, font size and clamp below is the value the corresponding old copy
// had, including the flex-shrink behaviour of the image column (no
// `flex-shrink-0`: on phones the 4/6 image and the full-width text column both
// shrink to fit, which is what made the real layout 40/60).
export const ArticleCardSkeleton = ({ page }: { page?: string }) => {
  if (page === "GAMES") {
    return (
      <div className="md:mb-8 mb-4 animate-pulse">
        <div className="flex flex-col w-full h-full gap-1 md:gap-2">
          <div className="w-full h-[130px] md:h-[174px] rounded-[4px] bg-theme-surface" />
          <div className="h-4 w-3/4 bg-theme-bg-secondary rounded px-1" />
        </div>
      </div>
    );
  }

  return (
    <div className="md:mb-8 mb-4 animate-pulse">
      <div className="flex w-full h-full border-b border-theme-border pb-3 md:pb-8 gap-3 lg:gap-5 overflow-auto md:overflow-visible">
        <div className="w-4/6 md:w-[259px] lg:w-[273px]">
          <div className="w-full rounded-[4px] bg-theme-surface" style={{ aspectRatio: "16 / 9" }} />
        </div>
        <div className="w-full md:w-2/3 flex flex-col justify-between gap-2 md:gap-3">
          <div className="flex flex-col gap-2">
            <div className="h-4 md:h-5 lg:h-6 w-3/4 bg-theme-bg-secondary rounded" />
            <div className="h-4 w-full bg-theme-bg-secondary rounded lg:hidden" />
            <div className="hidden lg:block h-4 w-full bg-theme-bg-secondary rounded" />
            <div className="hidden lg:block h-4 w-2/3 bg-theme-bg-secondary rounded" />
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-theme-bg-secondary flex-shrink-0" />
            <div className="h-3 w-12 md:w-20 lg:w-24 bg-theme-bg-secondary rounded" />
            <div className="hidden lg:block h-3 w-16 bg-theme-bg-secondary rounded" />
          </div>
        </div>
      </div>
    </div>
  );
};


const ArticleCard = (props: any) => {
  const coverImage = props.thumbnailResize || props.thumbnail || guideImage;
  const [thumbnailError, setThumbnailError] = useState(false);
  // `deferImage`: for lists that are entirely below the fold (the homepage
  // sections) the thumbnail mounts only when the card comes within 600px of the
  // viewport — see utils/useNearViewport.ts for why `loading="lazy"` is not
  // enough. The 16:9 box renders regardless, so nothing shifts.
  const cardRef = useRef<HTMLDivElement | null>(null);
  const near = useNearViewport(cardRef);
  const showImage = !props.deferImage || props.eagerImage || near;
  // No per-card /contents/:id/reactions fetch (it was one API call per card on
  // every list). The badge renders only when a parent passes `reactions`;
  // ReactionSummary renders nothing for empty counts.
  const reactionCounts: Record<string, number> = props.reactions ?? {};
  const thumbnailSrc = thumbnailError ? guideImage : coverImage;
  const imageLoading = props.eagerImage ? "eager" : "lazy";
  let ratingScore: number | undefined;
  if (props.category === "Review" || props.category === "ReviewBanner") {
    if (props.game[0]) {
      if (props.game[0].reviewScore) {
        ratingScore =
          (props.game[0].reviewScore.gameplay +
            props.game[0].reviewScore.graphicAndSound +
            props.game[0].reviewScore.performance +
            props.game[0].reviewScore.story) /
          4;
      } else if (props.game[0].newReviewScore) {
        ratingScore = props.game[0].newReviewScore.score;
      }
    }
  }
  const normalizedCategory = String(props.category ?? "").toLowerCase();
  const isReviewCategory =
    normalizedCategory === "review" || normalizedCategory === "reviewbanner";
  const currentReviewScore = Number(props.rating ?? ratingScore);
  const hasReviewScore = isReviewCategory && Number.isFinite(currentReviewScore);
  const reviewScoreText = hasReviewScore
    ? // One score rule site-wide: up to two decimals, no trailing zero (9, 9.5, 9.75).
      String(Math.round(currentReviewScore * 100) / 100)
    : "";
  const readTime: number | null = props.wordCount
    ? Math.max(1, Math.round(props.wordCount / 200))
    : props.readTime
    ? props.readTime
    : null;

  const currentViewCount = Number(
    props.view ?? props.viewCount ?? props.views ?? props.totalView ?? props.totalViews
  );
  const hasViewCount = Number.isFinite(currentViewCount) && currentViewCount >= 0;
  const formattedViewCount = hasViewCount
    ? Math.round(currentViewCount).toLocaleString()
    : "";

  const renderThumbnailMeta = () => {
    if (!hasViewCount && !hasReviewScore) return null;

    return (
      <>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-14 bg-gradient-to-t from-black/70 to-transparent" />
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-x-0 bottom-0 z-20 flex items-end gap-2 text-white ${
            hasReviewScore ? "justify-between" : "justify-end"
          }`}
        >
          {hasReviewScore ? (
            <div
              className="score-bug h-8 text-lg"
              title={`Review score: ${reviewScoreText}`}
              aria-label={`Review score: ${reviewScoreText}`}
            >
              {reviewScoreText}
            </div>
          ) : null}
          {hasViewCount ? (
            <div
              className="m-1.5 inline-flex h-6 items-center gap-1 bg-black/60 px-2 text-xs font-semibold leading-none"
              title={`Views: ${formattedViewCount}`}
              aria-label={`Views: ${formattedViewCount}`}
            >
              <FontAwesomeIcon
                icon={faEye}
                className="block h-[10px] w-[10px] shrink-0 align-middle"
              />
              <span className="block leading-none tabular-nums">
                {formattedViewCount}
              </span>
            </div>
          ) : null}
        </div>
      </>
    );
  };

  const handleThumbnailError = () => {
    if (!thumbnailError) {
      setThumbnailError(true);
    }
  };

  const saveData = {
    id: props.id,
    title: props.title,
    thumbnail: props.thumbnailResize || props.thumbnail,
    description: props.description,
    publishDt: props.publishDt,
    category: props.category,
    author: props.author?.[0] ? [props.author[0]] : [],
    game: props.game?.[0] ? [{ _id: props.game[0]._id, title: props.game[0].title }] : [],
  };

  // The row's dateline: "16:33" and "26 ต.ค. 2026", Bangkok time. From md the
  // date is split before the year so the rundown column stays narrow.
  const publishTime = formatBangkokTime(props.publishDt || "");
  const publishDate = formatThaiDate(props.publishDt || "");

  const href = "/article/" + props.id;
  const author = props.author?.[0];
  const authorHref = author
    ? `/tag/${author._id}?type=author&title=${encodeURIComponent(author.displayName ?? "")}`
    : "#";

  // The thumbnail, or — while deferred — its no-script fallback only.
  const renderThumbnail = (className: string, sizes: string) =>
    showImage ? (
      <Image
        src={thumbnailSrc}
        loader={cardImageLoader}
        alt=""
        className={className}
        fill
        sizes={sizes}
        loading={imageLoading}
        onError={handleThumbnailError}
      />
    ) : typeof thumbnailSrc === "string" ? (
      <noscript>
        <img src={thumbnailSrc} alt="" className={className} loading="lazy" />
      </noscript>
    ) : null;

  if (props.page === "GAMES") {
    // `compact` (Guide Builder content lists): a 16:9 thumbnail that follows the
    // column width instead of the fixed heights, and a smaller title.
    return (
      <div className="reveal-up group md:mb-8 mb-4" ref={cardRef}>
        <div className="flex flex-col w-full h-full gap-2">
          <a href={href} className="block">
            <div className={`w-full relative ${props.compact ? "aspect-[16/9]" : "h-[130px] md:h-[174px]"}`}>
              <ImageCard className="relative h-full">
                {renderThumbnail("w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]", "(max-width: 767px) 150px, 261px")}
                {renderThumbnailMeta()}
              </ImageCard>
            </div>
          </a>
          <div className="w-full">
            <a href={href}>
              <p className={`font-semibold leading-snug text-theme-text transition-colors group-hover:text-signal dark:group-hover:text-[#8DB4FF] ${props.compact ? "text-sm" : ""}`}>{props.title}</p>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reveal-up md:mb-8 mb-4" ref={cardRef}>
      <div className="flex w-full h-full border-b border-theme-border pb-3 md:pb-8 gap-3 lg:gap-5 overflow-auto md:overflow-visible">
        {/* image column: 4/6 of the row on phones (shrinking with the text
            column), the fixed card width from md up */}
        <div className="relative group w-4/6 md:w-[259px] lg:w-[273px]">
          <a href={href} aria-label={props.title} className="block">
            <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
              <ImageCard className="relative h-full">
                {renderThumbnail(
                  "object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]",
                  "(max-width: 767px) 66vw, (max-width: 1023px) 259px, 273px",
                )}
                {renderThumbnailMeta()}
              </ImageCard>
            </div>
          </a>
          <SaveButton article={saveData} variant="overlay" className="absolute top-2 right-2 z-30" />
        </div>

        {/* text column: stretched to the thumbnail's height, so
            `justify-between` puts the meta row level with the thumbnail's
            bottom edge. No `h-full` here — a percentage height disables the
            stretch (the row's height is indefinite) and the meta row then sat
            right under the title instead. */}
        <div className="flex flex-col justify-between w-full md:w-2/3">
          <div className="flex flex-col gap-2">
            <div className="flex-shrink-0">
              <p className="text-[15px] md:text-xl font-semibold leading-6 md:leading-7 line-clamp-2 lg:line-clamp-1 text-theme-text">
                <a href={href} className="transition-colors hover:text-signal dark:hover:text-[#8DB4FF]">{props.title}</a>
              </p>
              {/* phones show the reaction badge under the title; desktop moves
                  it to the meta row; the laptop layout never showed it */}
              <div className="md:hidden">
                <ReactionSummary counts={reactionCounts} />
              </div>
            </div>
            {/* `hidden` and `line-clamp-*` cannot share an element: the clamp
                utility sets `display: -webkit-box` later in the stylesheet and
                wins, so the description would render on phones too. */}
            <div className="hidden lg:block">
              <p className="max-w-[70ch] text-[15px] text-theme-text-secondary leading-6 line-clamp-2">
                {props.description}
              </p>
            </div>
            {/* the laptop layout's spacer where the description used to be */}
            <div className="hidden md:block lg:hidden mb-7" />
          </div>
          {/* meta row: the game tag (xl) and the like count (lg) on the left,
              author · date and time · read time on the right. Phones drop the
              time so the line still fits beside a 40% thumbnail. */}
          <div className="flex min-w-0 items-center justify-between gap-3">
            <div className="hidden lg:flex min-w-0 items-center gap-2.5">
              {props.game[0] ? (
                <div className="xl:flex hidden min-w-0">
                  <GameCapsuleTag
                    title={props.game[0].title}
                    gameId={props.game[0]._id}
                  />
                </div>
              ) : null}
              <ReactionSummary counts={reactionCounts} />
            </div>
            <div className="ml-auto flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap md:gap-2">
              <Link
                href={authorHref}
                className="relative w-5 h-5 md:w-6 md:h-6 cursor-pointer flex-shrink-0 before:content-[''] before:absolute before:-top-3 before:-bottom-3 before:-left-3 before:-right-3 md:before:-top-[10px] md:before:-bottom-[10px] md:before:-left-[10px] md:before:-right-[10px]"
                aria-label={`View articles by ${author?.displayName ?? ""}`}>
                <AuthorAvatar
                  src={author?.pictureProfile || DEFAULT_AVATAR}
                  sizes="(max-width: 767px) 20px, 24px"
                  alt=""
                  className="rounded-[20px]"
                />
              </Link>
              <Link
                href={authorHref}
                className="text-xs md:text-sm font-medium text-theme-text hover:text-signal dark:hover:text-[#8DB4FF] transition-colors leading-none">
                {author?.displayName}
              </Link>
              {publishDate ? (
                <>
                  <span aria-hidden="true" className="text-theme-text-secondary">·</span>
                  <time dateTime={props.publishDt} className="text-xs md:text-sm font-medium text-theme-text-secondary leading-none tabular-nums">
                    {publishDate}
                    {publishTime ? <span className="hidden sm:inline"> · {publishTime}</span> : null}
                  </time>
                </>
              ) : null}
              {readTime !== null && (
                <>
                  <span aria-hidden="true" className="hidden sm:inline text-theme-text-secondary">·</span>
                  <span className="hidden sm:inline-flex items-center gap-1 text-xs md:text-sm font-medium text-theme-text-secondary leading-none tabular-nums">
                    <FontAwesomeIcon icon={faClock} className="h-[9px] w-[9px] md:h-[10px] md:w-[10px]" />
                    {readTime} min read
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleCard;
